import type { OnboardingDraftStep } from "@graftus/schema";
import {
  ONBOARDING_SCREENS,
  isOnboardingScreen,
  screenIndex,
  type OnboardingScreen,
} from "@/types/onboarding";

/**
 * Which screen of the onboarding the URL may show, given what exists.
 *
 * The URL (`?s=`) stays the source of truth for "where am I"; this only
 * clamps it to what the state allows and picks a default when it is missing
 * or out of range:
 *
 * - no user: the welcome and the account step;
 * - a user without a business: sector and organisation (the business is
 *   created at the organisation step, so nothing later exists yet);
 * - a business that has not finished: organisation up to the step the server
 *   draft points at, so a reload resumes there and nothing is skipped;
 * - finished (Intelligence requested): the invite step and the done screen.
 */
export interface StepMachineInput {
  /** The raw `?s=` value, or null when absent. */
  requested: string | null;
  hasUser: boolean;
  hasBusiness: boolean;
  /** A sector is held in the browser (chosen before the business exists). */
  hasLocalSector: boolean;
  /** The server draft's current step, when a draft exists. */
  draftStep: OnboardingDraftStep | null;
  /** The signup is complete (`interestedProducts` is set). */
  finished: boolean;
  /** Dev builds: every screen reachable so each can be styled without signing up. */
  devUnlock?: boolean;
}

export interface StepMachineResult {
  screen: OnboardingScreen;
  min: OnboardingScreen;
  max: OnboardingScreen;
}

function draftScreen(step: OnboardingDraftStep | null): OnboardingScreen {
  if (step == null) return "organisation";
  return step === "done" ? "done" : step;
}

function clampTo(
  requested: string | null,
  min: OnboardingScreen,
  max: OnboardingScreen,
  fallback: OnboardingScreen,
): OnboardingScreen {
  if (!isOnboardingScreen(requested)) return fallback;
  const index = screenIndex(requested);
  if (index < screenIndex(min)) return min;
  if (index > screenIndex(max)) return max;
  return requested;
}

export function resolveScreen(input: StepMachineInput): StepMachineResult {
  if (input.devUnlock) {
    const first = ONBOARDING_SCREENS[0];
    const last = ONBOARDING_SCREENS[ONBOARDING_SCREENS.length - 1]!;
    return { screen: clampTo(input.requested, first, last, first), min: first, max: last };
  }

  if (!input.hasUser) {
    return {
      screen: clampTo(input.requested, "welcome", "account", "welcome"),
      min: "welcome",
      max: "account",
    };
  }

  if (!input.hasBusiness) {
    const fallback = input.hasLocalSector ? "organisation" : "sector";
    return {
      screen: clampTo(input.requested, "sector", "organisation", fallback),
      min: "sector",
      max: "organisation",
    };
  }

  if (!input.finished) {
    const max = draftScreen(input.draftStep);
    // A business that never finished cannot sit on "done".
    const reachable: OnboardingScreen = max === "done" ? "invite" : max;
    return {
      screen: clampTo(input.requested, "organisation", reachable, reachable),
      min: "organisation",
      max: reachable,
    };
  }

  const fallback: OnboardingScreen = input.draftStep === "done" ? "done" : "invite";
  return {
    screen: clampTo(input.requested, "invite", "done", fallback),
    min: "invite",
    max: "done",
  };
}
